/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Send, Close, Search } from '@mui/icons-material';

// Add local dependencies.
import {
  JoinRequestFormProps,
  JoinRequestFormData,
  SubmitJoinRequestRequest,
  StudioSearchResult,
} from '../../../types/joinRequest';
import { StudioRole } from '../../../types/studio';
import {
  useSubmitJoinRequestMutation,
  useSearchPublicStudiosQuery,
} from '../../../features/studios/studiosApi';
import { getRoleDisplayName } from '../../../utils/permissions';

/**
 * JoinRequestForm component provides a form for submitting join requests
 * to studios with studio search, role preference, and custom messages.
 */
const JoinRequestForm = ({
  onSuccess,
  onCancel,
  open = false,
  preselectedStudioId,
}: JoinRequestFormProps) => {
  const [formData, setFormData] = useState<JoinRequestFormData>({
    studioId: preselectedStudioId || '',
    message: '',
    requestedRole: StudioRole.MEMBER,
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof JoinRequestFormData, string>>
  >({});
  const [generalError, setGeneralError] = useState<string>('');
  const [studioSearchTerm, setStudioSearchTerm] = useState('');
  const [selectedStudio, setSelectedStudio] =
    useState<StudioSearchResult | null>(null);

  const [submitJoinRequest, { isLoading }] = useSubmitJoinRequestMutation();

  // Update studioId when preselectedStudioId changes
  useEffect(() => {
    if (preselectedStudioId) {
      setFormData(prev => ({ ...prev, studioId: preselectedStudioId }));
    }
  }, [preselectedStudioId]);

  // Search for public studios
  const { data: studioSearchResults, isLoading: isSearching } =
    useSearchPublicStudiosQuery(
      {
        search: studioSearchTerm,
        isPublic: true,
        allowJoinRequests: true,
        pageSize: 10,
      },
      {
        skip: studioSearchTerm.length < 2,
      }
    );

  const handleInputChange =
    (field: keyof JoinRequestFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
      // Clear general error when user starts typing
      if (generalError) {
        setGeneralError('');
      }
    };

  const handleRoleChange = (event: any) => {
    const role = event.target.value as StudioRole;
    setFormData(prev => ({ ...prev, requestedRole: role }));

    // Clear role error
    if (errors.requestedRole) {
      setErrors(prev => ({ ...prev, requestedRole: undefined }));
    }
    // Clear general error when user makes a selection
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleStudioSelect = (studio: StudioSearchResult | null) => {
    setSelectedStudio(studio);
    setFormData(prev => ({
      ...prev,
      studioId: studio?.id || '',
    }));

    // Clear studio error
    if (errors.studioId) {
      setErrors(prev => ({ ...prev, studioId: undefined }));
    }
    // Clear general error when user makes a selection
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof JoinRequestFormData, string>> = {};

    // Studio validation
    if (!formData.studioId.trim()) {
      newErrors.studioId = 'Please select a studio';
    } else if (!preselectedStudioId && selectedStudio?.isMember) {
      // Only check selectedStudio validations for manually selected studios
      newErrors.studioId = 'You are already a member of this studio';
    } else if (!preselectedStudioId && selectedStudio?.hasExistingRequest) {
      newErrors.studioId = 'You already have a pending request for this studio';
    }

    // Message validation (optional, but if provided must be valid)
    if (formData.message.trim() && formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less';
    }

    // Role validation
    if (!formData.requestedRole) {
      newErrors.requestedRole = 'Please select a role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // The backend takes the requester's name and email from their account
      const joinRequestData: SubmitJoinRequestRequest = {
        studioId: formData.studioId,
        message: formData.message.trim(),
        requestedRole: formData.requestedRole,
      };

      const result = await submitJoinRequest(joinRequestData).unwrap();

      // Reset form
      setFormData({
        studioId: preselectedStudioId || '',
        message: '',
        requestedRole: StudioRole.MEMBER,
      });
      setSelectedStudio(null);
      setStudioSearchTerm('');
      setErrors({});
      setGeneralError('');

      // Call success callback
      onSuccess?.(result);
    } catch (err) {
      // Enhanced error handling to show specific backend validation errors
      console.error('Failed to submit join request:', err);

      // Rate limited: show a friendly message instead of DRF's wait time
      if (
        err &&
        typeof err === 'object' &&
        'status' in err &&
        err.status === 429
      ) {
        setGeneralError(
          "You've sent several join requests recently. Please try again later."
        );
        return;
      }

      // Extract specific error messages from the backend response
      let errorMessage = 'Failed to submit join request. Please try again.';
      const fieldErrors: Partial<Record<keyof JoinRequestFormData, string>> =
        {};

      if (err && typeof err === 'object' && 'data' in err) {
        const errorData = err.data;

        if (errorData && typeof errorData === 'object') {
          // Handle field-specific errors
          if (
            'requested_role' in errorData &&
            Array.isArray(errorData.requested_role)
          ) {
            const [roleError] = errorData.requested_role;
            fieldErrors.requestedRole = roleError;
            errorMessage = `Role error: ${roleError}`;
          }
          if ('message' in errorData && Array.isArray(errorData.message)) {
            const [messageError] = errorData.message;
            fieldErrors.message = messageError;
            errorMessage = `Message error: ${messageError}`;
          }
          if (
            'non_field_errors' in errorData &&
            Array.isArray(errorData.non_field_errors)
          ) {
            const [nonFieldError] = errorData.non_field_errors;
            errorMessage = nonFieldError;
            fieldErrors.studioId = nonFieldError;
          }
          if ('detail' in errorData && typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
            fieldErrors.studioId = errorData.detail;
          }
        }
      }

      // Set the specific error messages
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...fieldErrors }));
        // Also set the general error for prominent display
        setGeneralError(errorMessage);
      } else {
        setErrors(prev => ({ ...prev, studioId: errorMessage }));
        setGeneralError(errorMessage);
      }
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      studioId: preselectedStudioId || '',
      message: '',
      requestedRole: StudioRole.MEMBER,
    });
    setSelectedStudio(null);
    setStudioSearchTerm('');
    setErrors({});
    setGeneralError('');
    onCancel?.();
  };

  const isFormValid = !isLoading;

  // Helper function to get submit button tooltip message
  const getSubmitButtonTooltip = () => {
    if (isLoading) return 'Submitting request...';
    return 'Submit your join request';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Send color="primary" />
          <Typography variant="h6">Request to Join Studio</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Error Alert */}
          {generalError && <Alert severity="error">{generalError}</Alert>}

          {/* Studio Selection */}
          {!preselectedStudioId && (
            <Autocomplete
              key={open ? 'autocomplete-open' : 'autocomplete-closed'}
              options={studioSearchResults?.results || []}
              getOptionLabel={option => option.name}
              value={selectedStudio}
              onChange={(event, newValue) => handleStudioSelect(newValue)}
              inputValue={studioSearchTerm}
              onInputChange={(event, newInputValue) =>
                setStudioSearchTerm(newInputValue)
              }
              loading={isSearching}
              renderInput={params => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Search Studios"
                  placeholder="Type to search for studios..."
                  error={!!errors.studioId}
                  helperText={errors.studioId}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search sx={{ mr: 1, color: 'text.secondary' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {isSearching ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  onClick={props.onClick}
                  onMouseOver={props.onMouseOver}
                  role={props.role}
                  aria-selected={props['aria-selected']}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      {option.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.memberCount} members
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                studioSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No studios found'
              }
              disabled={isLoading}
            />
          )}

          {/* Preselected Studio Display */}
          {preselectedStudioId && selectedStudio && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6">{selectedStudio.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedStudio.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedStudio.memberCount} members
              </Typography>
            </Box>
          )}

          {/* Role Requesting Selection */}
          <FormControl fullWidth error={!!errors.requestedRole}>
            <InputLabel>Role Requesting</InputLabel>
            <Select
              value={formData.requestedRole}
              onChange={handleRoleChange}
              label="Role Requesting"
              disabled={isLoading}
            >
              <MenuItem value={StudioRole.MANAGER}>
                {getRoleDisplayName(StudioRole.MANAGER)}
              </MenuItem>
              <MenuItem value={StudioRole.ADMIN}>
                {getRoleDisplayName(StudioRole.ADMIN)}
              </MenuItem>
              <MenuItem value={StudioRole.INSTRUCTOR}>
                {getRoleDisplayName(StudioRole.INSTRUCTOR)}
              </MenuItem>
              <MenuItem value={StudioRole.PARENT}>
                {getRoleDisplayName(StudioRole.PARENT)}
              </MenuItem>
              <MenuItem value={StudioRole.STUDENT}>
                {getRoleDisplayName(StudioRole.STUDENT)}
              </MenuItem>
              <MenuItem value={StudioRole.MEMBER}>
                {getRoleDisplayName(StudioRole.MEMBER)}
              </MenuItem>
            </Select>
            {errors.requestedRole && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 2 }}
              >
                {errors.requestedRole}
              </Typography>
            )}
          </FormControl>

          {/* Request Message */}
          <TextField
            label="Optional message"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleInputChange('message')}
            error={!!errors.message}
            helperText={
              errors.message || `${formData.message.length}/500 characters`
            }
            placeholder="Tell the studio administrators why you would like to join..."
            fullWidth
            disabled={isLoading}
          />

          {/* Info Alert */}
          <Alert severity="info">
            Your request will be reviewed by the studio administrators.
            You&apos;ll be notified when a decision is made.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<Close />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!isFormValid}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Send />
            )
          }
          title={getSubmitButtonTooltip()}
        >
          {isLoading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JoinRequestForm;
